/**
 * l1aTabs — the canonical 13-item tab model for the L1A project page.
 *
 * The merged stage-bar (Overview · the 11 L1A workflow stages · Settings) is
 * driven by ONE list, derived from the LOCKED engagementTemplate so the labels
 * can never drift from the workflow definition. Overview and Settings are the
 * two constant meta bookends; the middle 11 are the exact
 * `engagementTemplate.l1a.stages[].key` chain, in template order (2026-07-01:
 * the old `grids_charts` + `per_property` stages merged into a single
 * `evaluate` stage; the Astro-Vastu confirm-view stays between it and
 * comparative).
 *
 * Pure + dependency-light (only the template lookup). No React, no Firestore —
 * shared by the bar (ui.tsx `StageTabBar`), the Overview screen, and the
 * ProjectDetail routing controller so they all agree on one source.
 */
import { getEngagementTemplate } from './engagementTemplate';

/** The 11 middle stages — the exact engagementTemplate.l1a.stages keys.
 *  (`grids_charts` + `per_property` merged into `evaluate` on 2026-07-01.) */
export type L1AStageKey =
  | 'intake'
  | 'intake_gate'
  | 'evaluate'
  | 'astro_vastu'
  | 'comparative'
  | 'verdict'
  | 'owner_review'
  | 'rendered'
  | 'delivered'
  | 'closing_call'
  | 'post_delivery_followup';

/** A tab key = the 12 stages bracketed by the two meta bookends. */
export type L1ATabKey = 'overview' | L1AStageKey | 'settings';

/** Visual projection of a stage's rich state — the three dot/pill states. */
export type StageStatus = 'done' | 'now' | 'todo';

/** One item in the merged bar. */
export interface L1ATabItem {
  /** 'overview' | <stage key> | 'settings'. */
  key: L1ATabKey;
  /** Compact rail label (shortLabel of the full template label). */
  label: string;
  /** Full template label — drives the tooltip + the screen heading. */
  full: string;
  /** Overview/Settings — render an icon, no status dot. */
  meta?: boolean;
  /** Meta icon ('⌂' Overview, '⚙' Settings). */
  icon?: string;
  /** Settings is pushed to the far right (margin-left:auto). */
  sep?: boolean;
}

// The LOCKED L1A template is the read-only source for the 11 middle stages.
const L1A_TPL = getEngagementTemplate('L1A');
if (!L1A_TPL) {
  // Should never happen — L1A is a core tier. Fail loud in dev rather than
  // silently shipping an empty bar.
  throw new Error('l1aTabs: getEngagementTemplate("L1A") returned null');
}

/** The 11 stage keys, in template order. */
export const L1A_STAGE_KEYS = L1A_TPL.stages.map((s) => s.key) as L1AStageKey[];

/**
 * Compact-label map (mockup `.lbl`). Anything not listed falls through to the
 * full template label, so a new template stage degrades gracefully.
 */
const SHORT_LABELS: Partial<Record<string, string>> = {
  intake_gate: 'Gate',
  evaluate: 'Evaluate',
  astro_vastu: 'Astro-Vastu',
  comparative: 'Compare',
  rendered: 'Report',
  closing_call: 'Closing',
  post_delivery_followup: 'Follow-up',
};

/** Short rail label for a stage key (falls through to the full label). */
export function shortLabel(key: string, full: string): string {
  return SHORT_LABELS[key] ?? full;
}

/** The canonical 13-item tab model: Overview · 11 stages · Settings. */
export const L1A_TAB_ITEMS: L1ATabItem[] = [
  { key: 'overview', label: 'Overview', full: 'Overview', meta: true, icon: '⌂' },
  ...L1A_TPL.stages.map(
    (s): L1ATabItem => ({
      key: s.key as L1ATabKey,
      label: shortLabel(s.key, s.label),
      full: s.label,
    }),
  ),
  { key: 'settings', label: 'Settings', full: 'Settings', meta: true, icon: '⚙', sep: true },
];

const TAB_KEY_SET = new Set<string>(L1A_TAB_ITEMS.map((i) => i.key));

/**
 * RETIRED stage keys → their merged replacement. Old bookmarks / links using
 * `?tab=grids_charts` or `?tab=per_property` (the two stages merged into
 * `evaluate` on 2026-07-01) must not 404 or silently fall back to the default —
 * they alias to `evaluate`. Add future retirements here so links never break.
 */
const TAB_ALIASES: Record<string, L1ATabKey> = {
  grids_charts: 'evaluate',
  per_property: 'evaluate',
};

/**
 * Resolve a raw `?tab=` value through the retirement aliases, then validate.
 * Returns the canonical tab key, or null if it is neither a live key nor a
 * known alias (the caller then lands on the status-driven default).
 */
export function resolveL1ATabKey(v: string | null | undefined): L1ATabKey | null {
  if (v == null) return null;
  const aliased = TAB_ALIASES[v] ?? v;
  return TAB_KEY_SET.has(aliased) ? (aliased as L1ATabKey) : null;
}

/** Membership test for `?tab=` deep-links (accepts live keys AND retired
 *  aliases so old links resolve rather than 404). */
export function isL1ATabKey(v: string | null | undefined): v is L1ATabKey {
  return resolveL1ATabKey(v) != null;
}

/**
 * The tab to open when no (valid) `?tab=` is supplied. Mirrors `firstOpenStep`
 * over the 12 stages: first stage that is `now` (in progress) → else the first
 * `todo` stage → else `overview` (everything done = land on the calm summary).
 *
 * Pass 1 feeds an all-`todo` stub, so this returns the first stage (`intake`).
 * Pass 4's real `deriveL1AStageStates` projection makes it land on the active
 * stage (the demo seed sits at `evaluate`).
 */
export function defaultL1ATab(
  statusByKey: Partial<Record<string, StageStatus>>,
): L1ATabKey {
  const inProgress = L1A_STAGE_KEYS.find((k) => statusByKey[k] === 'now');
  if (inProgress) return inProgress;
  const todo = L1A_STAGE_KEYS.find((k) => statusByKey[k] === 'todo');
  if (todo) return todo;
  return 'overview';
}
